/**
 * AstrologyDataList Component
 * Universal component for displaying astrological data from multiple providers
 * Uses Chakra UI Table for proper layout with horizontal scroll in narrow mode
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
} from '@chakra-ui/react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { GrahaPosition } from '../../../../models/types';
import { LagnaData } from '../universal_architecture/core/PlanetDataModifier';
import { calculateLocalDegreesInHouse, formatLocalDegrees } from './planetFormatters';
import { ExpandIcon } from '../DataSections/ExpandIcon';
import PlanetDetails from './PlanetDetails';
import {
  TABLE_CONTAINER_STYLES,
  TABLE_TH_BASE_STYLES,
  TABLE_TD_BASE_STYLES,
} from '../DataSections/components/styles/tableStyles';
import './Planets.css';

// Sorting types
type SortColumn = 'position' | 'rashi' | 'nakshatra' | 'pada' | 'navamsa';
type SortDirection = 'asc' | 'desc';

export interface AstrologyDataListProps {
  planets?: GrahaPosition[];
  compact?: boolean;
  className?: string;
  title?: string;
  showLagna?: boolean;
  showPlanets?: boolean;
  showHouses?: boolean; // For future use
  showNakshatras?: boolean; // For future use
  apiResponse?: any; // Direct API response data (preferred over events)
  chartId?: string; // Chart ID for settings persistence
  shareMode?: boolean; // If true, disable interactive features (guest mode)
}

// Column configuration
const COLUMN_CONFIG = {
  expand: { minW: '24px', w: '24px' },
  name: { minW: '80px' },
  position: { minW: '108px' },
  rashi: { minW: '80px' },
  nakshatra: { minW: '108px' },
  pada: { minW: '50px' },
  navamsa: { minW: '80px' },
};

// Custom styles matching original design
const PLANET_TABLE_STYLES = {
  nameCell: {
    color: '#681515',
    fontWeight: 'normal',
    fontSize: '14px',
  },
  retrograde: {
    color: '#d32f2f',
    fontWeight: 'bold',
    marginLeft: '4px',
    fontSize: '10px',
  },
  cell: {
    color: '#333',
    fontWeight: 'normal',
    fontSize: '14px',
  },
  headerCell: {
    color: '#555',
    fontSize: '12px',
    fontWeight: 'normal',
  },
  expandedRow: {
    bg: '#fafafa',
  },
};

/**
 * Sort indicator component
 */
const SortIndicator: React.FC<{ direction: SortDirection }> = ({ direction }) => (
  <Box as="span" ml={1} display="inline-flex" alignItems="center">
    {direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
  </Box>
);

/**
 * Planet row component
 */
const PlanetRow: React.FC<{
  data: any;
  isExpanded: boolean;
  onToggle: () => void;
  chartId?: string;
  shareMode?: boolean;
}> = ({ data, isExpanded, onToggle, chartId, shareMode = false }) => {
  const localDegrees = formatLocalDegrees(calculateLocalDegreesInHouse(data.longitude || 0));

  return (
    <>
      <Tr
        onClick={onToggle}
        cursor="pointer"
        _hover={{ bg: '#f8f9fa' }}
        bg={isExpanded ? '#f8f9fa' : 'white'}
        transition="background 0.15s"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <Td {...TABLE_TD_BASE_STYLES} w={COLUMN_CONFIG.expand.w} minW={COLUMN_CONFIG.expand.minW} p={1}>
          <ExpandIcon isExpanded={isExpanded} size={12} />
        </Td>
        <Td {...TABLE_TD_BASE_STYLES} minW={COLUMN_CONFIG.name.minW} sx={PLANET_TABLE_STYLES.nameCell}>
          {data.name}
          {data.is_retrograde && (
            <Text as="span" sx={PLANET_TABLE_STYLES.retrograde}>℞</Text>
          )}
        </Td>
        <Td {...TABLE_TD_BASE_STYLES} minW={COLUMN_CONFIG.position.minW} sx={PLANET_TABLE_STYLES.cell}>{localDegrees}</Td>
        <Td {...TABLE_TD_BASE_STYLES} minW={COLUMN_CONFIG.rashi.minW} sx={PLANET_TABLE_STYLES.cell}>{data.rasi_name}</Td>
        <Td {...TABLE_TD_BASE_STYLES} minW={COLUMN_CONFIG.nakshatra.minW} sx={PLANET_TABLE_STYLES.cell}>{data.nakshatra_name}</Td>
        <Td {...TABLE_TD_BASE_STYLES} minW={COLUMN_CONFIG.pada.minW} textAlign="center" sx={PLANET_TABLE_STYLES.cell}>{data.nakshatra_pada}</Td>
        <Td {...TABLE_TD_BASE_STYLES} minW={COLUMN_CONFIG.navamsa.minW} sx={PLANET_TABLE_STYLES.cell}>{data.navamsa_rasi}</Td>
      </Tr>
      {isExpanded && (
        <Tr>
          <Td colSpan={7} p={0} bg="#fafafa" borderColor="gray.200">
            <Box px={4} py={2}>
              <PlanetDetails planetData={data} chartId={chartId} disabled={shareMode} />
            </Box>
          </Td>
        </Tr>
      )}
    </>
  );
};

/**
 * AstrologyDataList component - Universal astrology data display
 */
const AstrologyDataList: React.FC<AstrologyDataListProps> = ({
  planets: propsPlanets = [],
  compact = true,
  className = '',
  title = 'Astrological Data',
  showLagna = true,
  showPlanets = true,
  showHouses = false,
  showNakshatras = false,
  apiResponse = null,
  chartId,
  shareMode = false
}) => {
  // Expanded state for each planet
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Extract planet and lagna data from apiResponse
  const { planetData, lagnaData, isLoading, error } = useMemo(() => {
    if (!apiResponse?.graha_positions?.planets) {
      return {
        planetData: [] as any[],
        lagnaData: null as LagnaData | null,
        isLoading: false,
        error: apiResponse ? 'No planet data available' : 'No data provided'
      };
    }

    const allPlanetsFromApi = apiResponse.graha_positions.planets;
    const lagna = allPlanetsFromApi.find((p: any) => p.short_name === 'Lg');
    const planetsWithoutLagna = allPlanetsFromApi.filter((p: any) => p.short_name !== 'Lg');

    return {
      planetData: showPlanets ? planetsWithoutLagna : [],
      lagnaData: showLagna ? (lagna || null) : null,
      isLoading: false,
      error: null
    };
  }, [apiResponse, showPlanets, showLagna]);

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    column: SortColumn | null;
    direction: SortDirection;
  }>({ column: null, direction: 'asc' });

  // Sorting functions
  const sortFunctions = {
    position: (a: any, b: any) => {
      const aLocalDegrees = calculateLocalDegreesInHouse(a.longitude || 0);
      const bLocalDegrees = calculateLocalDegreesInHouse(b.longitude || 0);
      return sortConfig.direction === 'asc' ? aLocalDegrees - bLocalDegrees : bLocalDegrees - aLocalDegrees;
    },
    rashi: (a: any, b: any) => {
      const rashiOrder = ['Mesha', 'Vrishabha', 'Mithuna', 'Karka', 'Simha', 'Kanya', 'Tula', 'Vrishchika', 'Dhanu', 'Makara', 'Kumbha', 'Meena'];
      const aIndex = rashiOrder.indexOf(a.rasi_name) !== -1 ? rashiOrder.indexOf(a.rasi_name) : 999;
      const bIndex = rashiOrder.indexOf(b.rasi_name) !== -1 ? rashiOrder.indexOf(b.rasi_name) : 999;
      return sortConfig.direction === 'asc' ? aIndex - bIndex : bIndex - aIndex;
    },
    nakshatra: (a: any, b: any) => {
      const nakshatraOrder = ['Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashirsha', 'Ardra', 'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha', 'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishtha', 'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'];
      const aIndex = nakshatraOrder.indexOf(a.nakshatra_name) !== -1 ? nakshatraOrder.indexOf(a.nakshatra_name) : 999;
      const bIndex = nakshatraOrder.indexOf(b.nakshatra_name) !== -1 ? nakshatraOrder.indexOf(b.nakshatra_name) : 999;
      return sortConfig.direction === 'asc' ? aIndex - bIndex : bIndex - aIndex;
    },
    pada: (a: any, b: any) => {
      const aPada = a.nakshatra_pada || 1;
      const bPada = b.nakshatra_pada || 1;
      return sortConfig.direction === 'asc' ? aPada - bPada : bPada - aPada;
    },
    navamsa: (a: any, b: any) => {
      const rashiOrder = ['Mesha', 'Vrishabha', 'Mithuna', 'Karka', 'Simha', 'Kanya', 'Tula', 'Vrishchika', 'Dhanu', 'Makara', 'Kumbha', 'Meena'];
      const aIndex = rashiOrder.indexOf(a.navamsa_rasi) !== -1 ? rashiOrder.indexOf(a.navamsa_rasi) : 999;
      const bIndex = rashiOrder.indexOf(b.navamsa_rasi) !== -1 ? rashiOrder.indexOf(b.navamsa_rasi) : 999;
      return sortConfig.direction === 'asc' ? aIndex - bIndex : bIndex - aIndex;
    }
  };

  // Handle sorting
  const handleSort = useCallback((column: SortColumn | null) => {
    if (column === null) {
      setSortConfig({ column: null, direction: 'asc' });
    } else {
      setSortConfig(prev => ({
        column,
        direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
    }
  }, []);

  // Toggle expanded state
  const toggleExpanded = useCallback((shortName: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(shortName)) {
        next.delete(shortName);
      } else {
        next.add(shortName);
      }
      return next;
    });
  }, []);

  // Create unified data list for display
  const unifiedData = useMemo(() => {
    const allData: any[] = [];

    // Add Lagna if enabled and available
    if (showLagna && lagnaData) {
      allData.push({
        ...lagnaData,
        type: 'lagna',
        sortOrder: 0 // Lagna first
      });
    }

    // Add Planets if enabled and available
    if (showPlanets && planetData.length > 0) {
      allData.push(...planetData);
    }

    // Apply sorting if enabled
    if (sortConfig.column && sortFunctions[sortConfig.column]) {
      return allData.sort(sortFunctions[sortConfig.column]);
    } else {
      return allData.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    // eslint-disable-next-line
  }, [showLagna, lagnaData, showPlanets, planetData, sortConfig]);

  if (isLoading) {
    return (
      <Box className={`planets-container ${className}`} p={4} textAlign="center">
        <Text color="gray.500">Loading astrological data...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={`planets-container ${className}`} p={4} textAlign="center" bg="red.50" borderRadius="md">
        <Text color="red.600">{error}</Text>
      </Box>
    );
  }

  // Header click handler
  const HeaderCell: React.FC<{
    column: SortColumn | null;
    label: string;
    minW?: string;
    textAlign?: 'left' | 'center' | 'right';
  }> = ({ column, label, minW, textAlign = 'left' }) => (
    <Th
      {...TABLE_TH_BASE_STYLES}
      minW={minW}
      cursor="pointer"
      onClick={() => handleSort(column)}
      _hover={{ bg: '#e9ecef' }}
      userSelect="none"
      textAlign={textAlign}
      sx={PLANET_TABLE_STYLES.headerCell}
    >
      <Box display="flex" alignItems="center" justifyContent={textAlign === 'center' ? 'center' : 'flex-start'}>
        {label}
        {sortConfig.column === column && <SortIndicator direction={sortConfig.direction} />}
      </Box>
    </Th>
  );

  return (
    <Box mt={0} mb={0} maxW="100%" className={className}>
      <TableContainer
        {...TABLE_CONTAINER_STYLES}
        border="1px solid #e5e5e5"
        borderRadius="2px"
      >
        <Table size="sm" variant="simple" minW="550px">
          <Thead bg="#f8f9fa">
            <Tr>
              <Th {...TABLE_TH_BASE_STYLES} w={COLUMN_CONFIG.expand.w} minW={COLUMN_CONFIG.expand.minW} p={1} borderColor="#e5e5e5"></Th>
              <HeaderCell column={null} label="Name" minW={COLUMN_CONFIG.name.minW} />
              <HeaderCell column="position" label="Position" minW={COLUMN_CONFIG.position.minW} />
              <HeaderCell column="rashi" label="Rashi" minW={COLUMN_CONFIG.rashi.minW} />
              <HeaderCell column="nakshatra" label="Nakshatra" minW={COLUMN_CONFIG.nakshatra.minW} />
              <HeaderCell column="pada" label="Pada" minW={COLUMN_CONFIG.pada.minW} textAlign="center" />
              <HeaderCell column="navamsa" label="Navamsa" minW={COLUMN_CONFIG.navamsa.minW} />
            </Tr>
          </Thead>
          <Tbody>
            {unifiedData.map((item, index) => (
              <PlanetRow
                key={`${item.short_name || item.graha}-${index}`}
                data={item}
                isExpanded={expandedItems.has(item.short_name)}
                onToggle={() => toggleExpanded(item.short_name)}
                chartId={chartId}
                shareMode={shareMode}
              />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};


export default AstrologyDataList;
